import { autoUpdate, computePosition, flip, shift } from "@floating-ui/dom";
import { registerAnchorCleanup } from "../magics/anchor";

export default () => ({
  open: false,

  init() {
    if (this.$el.dataset.teleport === "true") {
      this.$nextTick(() => this.$dialogTeleport(this.$refs.floatingElement));
    }
  },

  /**
   * Shows or hides the floating element in the top layer, so that it draws
   * over the rest of the page and is not cut off by its container. Browsers
   * without `togglePopover` keep the earlier behavior.
   */
  showInTopLayer(show: boolean): void {
    const floatingElement = this.$refs.floatingElement as
      | (HTMLElement & {
          togglePopover?: (options: { force: boolean }) => void;
        })
      | undefined;

    if (!floatingElement?.isConnected) return;

    // Unhide first, or the browser ignores the call and the menu flashes.
    if (show) floatingElement.style.removeProperty("display");

    floatingElement.togglePopover?.({ force: show });
  },

  openPopover() {
    this.open = true;
    this.showInTopLayer(true);
    this.$dispatch("popover:toggle", {
      open: true,
    });
  },

  closePopover() {
    this.open = false;
    this.showInTopLayer(false);
    this.$dispatch("popover:toggle", {
      open: false,
    });
  },

  /**
   * Opens the popover at the given screen position and focuses the first menu
   * item. The element stays where it is in the page, because the top layer
   * already draws it over the rest of the page. Used by the
   * x-popover-context-zone directive.
   */
  openAtCoordinates(clientX: number, clientY: number): void {
    const floatingElement = this.$refs.floatingElement as HTMLElement;

    const alpine = (
      window as unknown as { Alpine?: { $data: (el: Element) => unknown } }
    ).Alpine;
    document
      .querySelectorAll<HTMLElement>('[x-data="popover"]')
      .forEach((el) => {
        if (el === this.$el) return;
        const data = alpine?.$data(el) as
          { open?: boolean; closePopover?: () => void } | undefined;
        if (data?.open) data.closePopover?.();
      });

    this.openPopover();

    const virtualElement = {
      getBoundingClientRect: () => ({
        x: clientX,
        y: clientY,
        top: clientY,
        left: clientX,
        right: clientX,
        bottom: clientY,
        width: 0,
        height: 0,
        toJSON: () => undefined,
      }),
    };

    let initialFocusApplied = false;

    // Stops a position update thats still in progress when the popover closes
    let cleaned = false;

    /**
     * Places the menu at the cursor and focuses its first item. Runs again
     * each time the element moves, because the first run measures it while it
     * is still hidden and cannot tell whether it fits on screen.
     */
    const reposition = (): void => {
      computePosition(virtualElement, floatingElement, {
        placement: "bottom-start",
        strategy: "fixed",
        middleware: [flip(), shift()],
      }).then(({ x, y }) => {
        if (cleaned) return;
        Object.assign(floatingElement.style, {
          position: "fixed",
          left: `${x}px`,
          top: `${y}px`,
        });
        if (!initialFocusApplied) {
          initialFocusApplied = true;
          const within = this.$focus.within(floatingElement) as {
            getFirst?: () => HTMLElement | undefined;
          };
          within.getFirst?.()?.focus({
            focusVisible: true,
            preventScroll: true,
          } as FocusOptions);
        }
      });
    };

    const stopAutoUpdate = autoUpdate(
      virtualElement,
      floatingElement,
      reposition
    );

    /**
     * Stops tracking the cursor and listening for the close event.
     */
    const cleanup = (): void => {
      cleaned = true;
      stopAutoUpdate();
      this.$el.removeEventListener("popover:toggle", onToggle);
    };

    /**
     * Clears the cursor position when the popover closes. Only on a real
     * close, so that the next click on the trigger does not show the menu at
     * the old cursor position for a moment.
     */
    const onToggle = (event: Event): void => {
      const detail = (event as CustomEvent<{ open: boolean }>).detail;
      if (detail?.open !== false) return;
      cleanup();
      floatingElement.style.removeProperty("position");
      floatingElement.style.removeProperty("left");
      floatingElement.style.removeProperty("top");
    };
    this.$el.addEventListener("popover:toggle", onToggle);

    // Lets the next open of this menu shut this one down before it starts
    registerAnchorCleanup(floatingElement, cleanup);
  },
});
